import _, { isEmpty } from 'lodash'
import { truncate } from 'lodash/fp'
import React, { useEffect, useState } from 'react'
import { useCart } from 'react-use-cart'

import {
  getCoinCalculation,
  getPreschoolPaymentSummary,
  getProgramCost
} from 'api'
import constants from 'app/constants'
import { confirm, showToast } from 'app/helpers'

import Actions from './components/Actions'
import AddProgramItemToCartModal from './components/AddProgramItemToCartModal'
import AddToCartModal from './components/AddToCartModal' // Import the new modal
import BatchDetails from './components/BatchDetails'
import BookDemoClass from './components/BookDemoClass'
import CartSlider from './components/Cart'
import CheckoutModal from './components/CheckoutModal'
import MembershipPreview from './components/MembershipPreview'
import { useBatchSelection } from './hooks/useBatchSelection'

import {
  batchTypesMap,
  BookingTypes,
  batchTypesNewFlow,
  BOOKING_PAYMENT_CTA_TEXT
} from '../../constants'
import { transformCartItemsPaymentSummaryPayload } from '../../transformers'

const ModalsAndActions = (props) => {
  const {
    selectedBatchType,
    isActiveCareOrPreschool,
    selectedProgram,
    deselectProgram,
    selectedCentres
  } = props
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [bookingType, setBookingType] = useState(null)

  const batchTypesNewFlowValues = Object.values(batchTypesNewFlow)
  const isNewFlow = batchTypesNewFlowValues.includes(selectedBatchType)

  const { selectedBatches, deselectBatch } = useBatchSelection()
  const [cartItems, setCartItems] = useState([])

  const [isMembershipPreviewOpen, setIsMembershipPreviewOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)

  const [checkoutType, setCheckoutType] = useState(null)

  const [paymentOptions, setPaymentOptions] = useState([])
  const [coinSummary, setCoinSummary] = useState({
    expires_at: null,
    new_wallet_validity: null,
    remaining_coins: null
  })
  const [excelPaymentSummary, setExcelPaymentSummary] = useState([])
  // Next month's recurring excel bill (existing subscriptions + this cart). The
  // e-mandate checkouts quote it, because that — not today's charge — is what the
  // mandate limit has to clear.
  const [upcomingExcelPaymentSummary, setUpcomingExcelPaymentSummary] =
    useState([])
  const [kitItems, setKitItems] = useState([])
  const [ctaOptions, setCtaOptions] = useState([])

  const {
    items: newFlowCartItems,
    metadata: cartMetadata,
    updateItem
  } = useCart()

  useEffect(() => {
    const cartOpenHandler = () => {
      if (isNewFlow) {
        onBatchClick()
        return
      }

      switch (selectedBatchType) {
        case batchTypesMap.explore:
          onDemoBookClick()
          break
        case batchTypesMap.exploration:
          onBookExplorationClick()
          break
        case batchTypesMap.excel:
          onBookExcelClick()
          break
        case batchTypesMap.class:
          onClassBookClick()
          break
      }
    }
    if (selectedBatches.length > 0) cartOpenHandler()
  }, [selectedBatches])

  useEffect(() => {
    if (!isEmpty(selectedProgram)) {
      setIsModalOpen(true)
    }
  }, [selectedProgram])

  const onHandleModalClose = () => {
    setIsModalOpen(false)
    setBookingType(null)
    deselectBatch({ bulk: true })
    deselectProgram()
    setEditingItem(false)
  }

  const onBatchClick = () => {
    setIsModalOpen(true)
  }

  const onClassBookClick = () => {
    setCartItems([...selectedBatches])
    setBookingType(BookingTypes.CLASS)
    setIsModalOpen(true)
  }

  const onDemoBookClick = () => {
    setCartItems([...selectedBatches])
    if (selectedBatchType === 'explore') setBookingType(BookingTypes.MEMBERSHIP)
    else setBookingType(BookingTypes.DEMO)
    setIsModalOpen(true)
  }

  const onBookExplorationClick = () => {
    setCartItems([...selectedBatches])
    setBookingType(BookingTypes.EXPLORATION)
    setIsModalOpen(true)
  }

  const onBookExcelClick = () => {
    setCartItems([...selectedBatches])
    setBookingType(BookingTypes.EXCEL)
    setIsModalOpen(true)
  }

  const onMembershipPreviewBookClick = () => {
    setCartItems([...selectedBatches])
    setIsMembershipPreviewOpen(true)
  }

  const onMembershipBookClick = () => {
    setCartItems([...selectedBatches])
    setBookingType(BookingTypes.MEMBERSHIP)

    const totalSelections = _.countBy(selectedBatches, 'course.uuid')

    let flag = true
    selectedBatches.forEach((item) => {
      flag =
        flag &&
        totalSelections[item.course.uuid] === item.course.batches_per_booking
    })

    if (!flag) {
      confirm(
        () => setIsModalOpen(true),
        'We advise you to book twice a week classes per regular class, for a good educational experience.',
        () => {}
      )
    } else {
      setIsModalOpen(true)
    }
  }

  const onMembershipPreviewClose = () => {
    setIsMembershipPreviewOpen(false)
  }

  const onBookingSuccess = () => {
    setCartItems([])
  }

  const onDeleteCartItem = (item) => {
    setCartItems((state) => {
      const updatedCartItems = state.filter((batch) => batch.uuid !== item.uuid)

      if (!updatedCartItems.length) onHandleModalClose()
      else deselectBatch(item)

      return updatedCartItems
    })
  }

  const onCartItemEditHandler = (item) => {
    setEditingItem(item)
    setIsModalOpen(true)
  }

  const checkoutButtonClickHandler = (type) => {
    setCheckoutType(type)
  }

  useEffect(() => {
    if (newFlowCartItems.length === 0) return

    const fetchCartData = async () => {
      const cartType = cartMetadata?.cartType || 'excel'

      // An item added before a parent/child was picked carries no userInfo.
      // Reading through it used to throw while building the request — which
      // left payment_options unset and the drawer with no pay buttons and no
      // explanation. Fail loudly on the cart instead, and never quote a price
      // off a payload that silently dropped a line.
      const incompleteItems = newFlowCartItems.filter(
        (item) =>
          !item?.userInfo?.student?.user?.uuid || !item?.userInfo?.parent?.uuid
      )
      if (incompleteItems.length > 0) {
        setPaymentOptions([])
        showToast(
          'A cart item is missing its child. Remove it and add the batch again to continue.',
          constants.ERROR
        )
        return
      }

      const parentUuid = newFlowCartItems?.[0]?.userInfo?.parent?.uuid

      if (cartType === 'excel') {
        // Excel flow - Use coin calculation API
        try {
          const coinCalculationResponse = await getCoinCalculation({
            cart_type: 'booking',
            parent: parentUuid,
            cartItems: newFlowCartItems
          })

          const coinCalculationData = coinCalculationResponse?.data
          const {
            expires_at,
            new_wallet_validity,
            remaining_coins,
            total_spending,
            nudges
          } = coinCalculationData

          console.log('coinCalculationData', coinCalculationData)

          const coinSummary = {
            expires_at,
            new_wallet_validity,
            remaining_coins,
            monthly_consumption: total_spending,
            nudges
          }

          if (coinCalculationResponse?.success) {
            setPaymentOptions(coinCalculationData.payment_options)
            setCoinSummary(coinSummary)
            setExcelPaymentSummary(
              coinCalculationData?.excel_payment_summary || []
            )
            setUpcomingExcelPaymentSummary(
              coinCalculationData?.upcoming_excel_payment_summary || []
            )
            setKitItems(coinCalculationData?.upcoming_kits_list)
          }
        } catch (error) {
          console.error('Error fetching excel cart data:', error)
          setPaymentOptions([])
          // Same reasoning as payment_options: a stale recurring figure alongside a
          // failed reprice would quote the parent a mandate limit for a cart that no
          // longer exists.
          setUpcomingExcelPaymentSummary([])
          showToast(
            'Could not load payment options for this cart. Please retry.',
            constants.ERROR
          )
        }
      } else if (cartType === 'school' || cartType === 'care') {
        try {
          const paymentSummaryPayload = transformCartItemsPaymentSummaryPayload(
            newFlowCartItems,
            parentUuid
          )

          // preschool/care payment summary API
          const preschoolPaymentResponse = await getPreschoolPaymentSummary({
            payload: paymentSummaryPayload
          })

          if (preschoolPaymentResponse?.status === 200) {
            const paymentData = preschoolPaymentResponse?.data?.payment_summary

            // Set payment options
            const paymentOptions = [
              {
                payment_type: 'program_rupees',
                summary: paymentData
              }
            ]

            setPaymentOptions(paymentOptions)
            // Updating cart items with prices from payment summary
            if (paymentData) {
              const paymentSummaryItems = paymentData.slice(0, -1)

              // Mapping the cart items to their transformed item_ids
              newFlowCartItems.forEach((cartItem) => {
                // Find matching payment summary item
                const paymentItem = paymentSummaryItems.find(
                  (summaryItem) => summaryItem.id === cartItem.id
                )
                if (paymentItem) {
                  // Get the final_total line item for this program
                  const finalTotalItem = paymentItem.line_items.find(
                    (item) => item.type === 'final_total'
                  )

                  // Update the cart item with the final_total price
                  if (finalTotalItem) {
                    updateItem(cartItem.id, {
                      ...cartItem,
                      price: finalTotalItem.value,
                      isPriceUpdated: true
                    })
                  }
                }
              })
            }
          }
        } catch (error) {
          console.error(`Error fetching ${cartType} payment summary:`, error)
        }
      }
    }

    fetchCartData()
  }, [newFlowCartItems.length, cartMetadata?.cartType])

  useEffect(() => {
    // No options for this cart means no buttons for this cart. Leaving the
    // previous cart's ctaOptions in place showed pay buttons priced off a
    // summary that no longer applied.
    if (!paymentOptions || paymentOptions.length <= 0) {
      setCtaOptions([])
    } else {
      setCtaOptions(
        paymentOptions?.map((option, index) => {
          return {
            text: BOOKING_PAYMENT_CTA_TEXT[option.payment_type],
            type: index === 0 ? 'primary' : 'secondary',
            payment_type: option.payment_type
          }
        })
      )
    }
  }, [paymentOptions])

  return isNewFlow ? (
    <>
      {isActiveCareOrPreschool ? (
        <AddProgramItemToCartModal
          isModalOpen={isModalOpen}
          bookingType={bookingType}
          selectedProgram={selectedProgram}
          onHandleModalClose={() => {
            setIsModalOpen(false)
            setBookingType(null)
            deselectProgram()
          }}
          selectedBatchType={selectedBatchType}
          selectedCentres={selectedCentres}
        />
      ) : (
        <AddToCartModal
          isModalOpen={isModalOpen}
          bookingType={bookingType}
          selectedItem={selectedBatches?.[0]}
          onHandleModalClose={onHandleModalClose}
          deselectBatch={deselectBatch}
          setIsModalOpen={setIsModalOpen}
          selectedBatchType={selectedBatchType}
          isEditing={!!editingItem}
          editingItem={editingItem}
        />
      )}
      <CartSlider
        coinSummary={coinSummary}
        excelPaymentSummary={excelPaymentSummary}
        onCartItemEditHandler={onCartItemEditHandler}
        checkoutButtonClickHandler={checkoutButtonClickHandler}
        ctaOptions={ctaOptions}
      />
      <CheckoutModal
        coinSummary={coinSummary}
        isModalOpen={!!checkoutType}
        onModalClose={() => {
          setCheckoutType(null)
        }}
        checkoutType={checkoutType}
        paymentOptions={paymentOptions}
        excelPaymentSummary={excelPaymentSummary}
        upcomingExcelPaymentSummary={upcomingExcelPaymentSummary}
        kitItems={kitItems}
      />
      <BatchDetails />
    </>
  ) : (
    <>
      <Actions
        cartItemsLength={selectedBatches.length}
        isDisabled={isEmpty(selectedBatches)}
        onDemoBookClick={onDemoBookClick}
        onClassBookClick={onClassBookClick}
        onBookExplorationClick={onBookExplorationClick}
        onBookExcelClick={onBookExcelClick}
        onMembershipPreviewBookClick={onMembershipPreviewBookClick}
        onMembershipBookClick={onMembershipBookClick}
        selectedBatchType={selectedBatchType}
        isNewFlow={isNewFlow}
      />
      <BookDemoClass
        isModalOpen={isModalOpen}
        bookingType={bookingType}
        cartItems={cartItems}
        onBookingSuccess={onBookingSuccess}
        onDeleteCartItem={onDeleteCartItem}
        onHandleDemoClassCloseClick={onHandleModalClose}
        deselectBatch={deselectBatch}
        setIsBookDemoClassModalOpen={setIsModalOpen}
        selectedBatchType={selectedBatchType}
        setCartItems={setCartItems}
        setBookingType={setBookingType}
      />
      <BatchDetails />
      <MembershipPreview
        cartItems={cartItems}
        onDelete={onDeleteCartItem}
        isOpen={isMembershipPreviewOpen}
        onClose={onMembershipPreviewClose}
      />
    </>
  )
}

export default ModalsAndActions
